import type { ReasoningPurpose, ReasoningRecord, ReasoningSession } from "~/api/reasoningTypes";

/**
 * The derivations every reasoning surface shares — the Hub, the run tab and the criterion review
 * used to carry their own copies of "the latest candidate and the evaluation that matches it".
 * `candidateDraft.ts` keeps its per-name `latestCandidate(session, logicalId)`; this one is the
 * session's newest candidate regardless of name.
 */

/** A direct request's job statuses after which `requestStatus` will never change again. */
export const TERMINAL_JOB_STATUSES = ["completed", "failed", "cancelled", "canceled", "uncertain"];
/** A run's statuses after which the run itself will not move. */
export const TERMINAL_RUN_STATUSES = ["completed", "completed-degraded", "failed", "cancelled"];

export const isTerminalJobStatus = (status: string): boolean =>
  TERMINAL_JOB_STATUSES.includes(status.toLowerCase());
export const isTerminalRunStatus = (status: string): boolean =>
  TERMINAL_RUN_STATUSES.includes(status.toLowerCase());

export function latestCandidate(
  session: ReasoningSession | null | undefined,
): ReasoningRecord | null {
  return session?.records.filter((row) => row.kind === "candidate").at(-1) ?? null;
}

/** The newest evaluation of exactly this candidate under the session's current context and criteria. */
export function matchingEvaluation(
  session: ReasoningSession | null | undefined,
  candidate: ReasoningRecord | null | undefined,
): ReasoningRecord | null {
  if (!session || !candidate) return null;
  return (
    session.records
      .filter(
        (row) =>
          row.kind === "evaluation" &&
          row.payload.candidateRecordId === candidate.id &&
          row.payload.contextRecordId === session.contextRecordId &&
          row.payload.criteriaDigest === session.criteriaDigest,
      )
      .at(-1) ?? null
  );
}

/** The engine's record for the latest answer, its check, and whether the reader accepted it. */
export function readSession(session: ReasoningSession | null | undefined) {
  const candidate = latestCandidate(session);
  const evaluation = matchingEvaluation(session, candidate);
  return {
    candidate,
    evaluation,
    accepted: !!session?.currentAcceptanceRecordId,
    qualified: evaluation?.payload.qualified === true,
  };
}

/** The pinned context's declared purpose, when the context carries one. */
export function purposeOf(session: ReasoningSession | null | undefined): ReasoningPurpose | null {
  const value = session?.records.find((row) => row.id === session.contextRecordId)?.payload.purpose;
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as ReasoningPurpose)
    : null;
}

/** The pinned context's assembly manifest (scope, query, model, sources…), when present. */
export function contextManifest(
  session: ReasoningSession | null | undefined,
): Record<string, unknown> | null {
  const value = session?.records.find((row) => row.id === session.contextRecordId)?.payload
    .manifest;
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

export type ReasoningPhase = "waiting" | "thinking" | "done" | "failed" | "stopped" | "uncertain";
/** The engine's job statuses, folded into the six things a reader needs to tell apart. */
export function phaseOf(jobStatus: string, evaluated: boolean, errored: boolean): ReasoningPhase {
  const status = jobStatus.toLowerCase();
  if (status === "uncertain") return "uncertain";
  if (status === "cancelled" || status === "canceled") return "stopped";
  if (status === "failed" || status === "error" || errored) return "failed";
  if (status === "completed" || status === "generated" || status === "done" || evaluated)
    return "done";
  if (status === "claimed" || status === "running") return "thinking";
  return "waiting";
}

export function plainRunStatus(status: string, t: (key: string) => string): string {
  switch (status.toLowerCase()) {
    case "completed":
    case "completed-degraded":
      return t("Answered");
    case "running":
    case "observing":
      return t("Thinking");
    case "failed":
      return t("Failed");
    case "cancelled":
      return t("Stopped");
    case "paused":
      return t("Paused");
    default:
      return t("Waiting");
  }
}
